import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { format as formatDate } from "date-fns";
import { useTenant } from "@/contexts/TenantContext";
import { useDateLocale } from "@/lib/dateLocale";
import { useSession } from "@/hooks/useSession";
import { useRealtimeSeats } from "@/hooks/useRealtimeSeats";
import { publicApi } from "@/lib/api";
import { trackPageView, trackEvent } from "@/lib/analytics";
import { SEO, SEOPresets } from "@/components/shared/SEO";
import { parseError, isSeatConflictError, isRouteChangedError } from "@/lib/error-handler";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { ErrorMessage } from "@/components/shared/ErrorMessage";
import { HoldTimer } from "@/components/shared/HoldTimer";
import { SeatLegend } from "@/components/seats/SeatLegend";
import { SeatGrid } from "@/components/seats/SeatGrid";
import { EnhancedHeader } from "@/components/layout/EnhancedHeader";
import {
  formatInTimezone,
  getTimezoneAbbreviation,
  DEFAULT_TIMEZONE,
} from "@shared/timezone-utils";
import type { SeatStatus } from "@/lib/seat-utils";

interface SeatData {
  seatNumber: string;
  status: SeatStatus;
}

interface SeatAvailabilityResponse {
  tripId: number;
  totalSeats: number;
  rows: number;
  seatsPerRow: number;
  seatLayout: unknown;
  seats: SeatData[];
}

interface City {
  id: number;
  name: string;
  state: string | null;
}

interface TripDetails {
  id: number;
  departureTime: string;
  arrivalTime: string | null;
  availableSeats: number;
  segmentId: number;
  segmentPrice: string;
  roundTripPrice: string | null;
  routeName: string;
  /**
   * The PASSENGER's own boarding/alighting for this segment, resolved
   * server-side (PR B3) by the same code that produces the ticket. Rendering
   * these keeps search, seat selection, checkout and the ticket showing one
   * consistent set of times. Null = genuinely unknown, never a substitute.
   */
  boardingIso: string | null;
  boardingTimeFormatted: string | null;
  boardingTimezone: string;
  boardingAbbr: string | null;
  alightingIso: string | null;
  alightingTimeFormatted: string | null;
  alightingTimezone: string;
  alightingAbbr: string | null;
}

interface SeatHoldResponse {
  id: number;
  seatNumber: string;
  expiresAt: string;
}

export function SeatSelectionPage() {
  const { t } = useTranslation();
  const dl = useDateLocale();
  const { slug, tenant } = useTenant();
  const { sessionId } = useSession();
  const [, setLocation] = useLocation();
  const params = useParams<{ tripId: string }>();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const queryClient = useQueryClient();

  const tripId = params.tripId ? parseInt(params.tripId, 10) : null;
  const passengers = parseInt(searchParams.get("passengers") || "1", 10);
  const date = searchParams.get("date") || "";
  const fromId = searchParams.get("from") || "";
  const toId = searchParams.get("to") || "";
  const tripType = searchParams.get("tripType") || "one-way";
  const price = searchParams.get("price") || "";

  // Local state for selected seats and holds
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [ownHeldSeats, setOwnHeldSeats] = useState<string[]>([]);
  const [holdExpiresAt, setHoldExpiresAt] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [seatError, setSeatError] = useState<{
    type: "default" | "conflict" | "rate-limit" | "network" | "session";
    message: string;
    title?: string;
    retryAfter?: number;
  } | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Stable callbacks for WebSocket events (prevent unnecessary reconnections)
  const handleWsSeatHold = useCallback((seatNumbers: string[]) => {
    // Optimistically update seat status to 'held' for instant UI feedback
    queryClient.setQueryData<SeatAvailabilityResponse | null>(
      ["seats", slug, tripId],
      (old) => {
        if (!old) return old;
        return {
          ...old,
          seats: old.seats.map((seat) =>
            seatNumbers.includes(seat.seatNumber)
              ? { ...seat, status: "held" as SeatStatus }
              : seat
          ),
        };
      }
    );
  }, [queryClient, slug, tripId]);

  const handleWsSeatRelease = useCallback((seatNumbers: string[]) => {
    // Optimistically update seat status to 'available' for instant UI feedback
    queryClient.setQueryData<SeatAvailabilityResponse | null>(
      ["seats", slug, tripId],
      (old) => {
        if (!old) return old;
        return {
          ...old,
          seats: old.seats.map((seat) =>
            seatNumbers.includes(seat.seatNumber)
              ? { ...seat, status: "available" as SeatStatus }
              : seat
          ),
        };
      }
    );
  }, [queryClient, slug, tripId]);

  const handleWsSeatExpire = useCallback((seatNumbers: string[]) => {
    // Optimistically update expired seats to 'available'
    queryClient.setQueryData<SeatAvailabilityResponse | null>(
      ["seats", slug, tripId],
      (old) => {
        if (!old) return old;
        return {
          ...old,
          seats: old.seats.map((seat) =>
            seatNumbers.includes(seat.seatNumber)
              ? { ...seat, status: "available" as SeatStatus }
              : seat
          ),
        };
      }
    );
  }, [queryClient, slug, tripId]);

  const handleWsConnectionChange = useCallback((connected: boolean) => {
    setWsConnected(connected);
  }, []);

  // Track page view
  useEffect(() => {
    if (slug) {
      trackPageView("seat_selection", slug);
    }
  }, [slug]);

  // Real-time seat updates via WebSocket
  useRealtimeSeats({
    tenantSlug: slug,
    tripId,
    sessionId,
    onSeatHold: handleWsSeatHold,
    onSeatRelease: handleWsSeatRelease,
    onSeatExpire: handleWsSeatExpire,
    onConnectionChange: handleWsConnectionChange,
  });

  // Fetch cities for display
  const { data: cities = [] } = useQuery({
    queryKey: ["cities", slug],
    queryFn: async () => {
      if (!slug) return [];
      return publicApi.get<City[]>(`/api/public/${slug}/cities`);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch trip search results to get trip details (price, route name, times)
  const { data: trips = [] } = useQuery({
    queryKey: ["trips", slug, date, fromId, toId, passengers],
    queryFn: async () => {
      if (!slug || !date || !fromId || !toId) return [];
      const queryParams = new URLSearchParams({
        originCityId: fromId,
        destinationCityId: toId,
        date,
        passengers: passengers.toString(),
      });
      return publicApi.get<TripDetails[]>(
        `/api/public/${slug}/trips/search?${queryParams.toString()}`
      );
    },
    enabled: !!slug && !!date && !!fromId && !!toId,
    staleTime: 30 * 1000,
  });

  // Fetch seat availability
  const {
    data: seatData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["seats", slug, tripId],
    queryFn: async () => {
      if (!slug || !tripId) return null;
      return publicApi.get<SeatAvailabilityResponse>(
        `/api/public/${slug}/trips/${tripId}/seats`
      );
    },
    enabled: !!slug && !!tripId,
    staleTime: 10 * 1000, // 10 seconds - seats change frequently
    // Only poll when WebSocket is disconnected (graceful degradation)
    refetchInterval: wsConnected ? false : 30 * 1000,
  });

  // Get trip details from search results
  const tripDetails = trips.find((t) => t.id === tripId);

  // Get city names
  const originCity = cities.find((c) => c.id.toString() === fromId);
  const destinationCity = cities.find((c) => c.id.toString() === toId);

  // Get tenant timezone for display
  const tenantTimezone = tenant?.timezone || DEFAULT_TIMEZONE;

  // The date must come from the BOARDING instant, not the search parameter.
  // `date` is the day the route departs; on an overnight leg the passenger
  // boards on a different one, so showing their 3:00 AM boarding clock beside
  // the search date told them the right time on the wrong day.
  //
  // The search parameter is still what we FETCH with — only the display moves.
  let displayDate = date;
  try {
    if (tripDetails?.boardingIso) {
      displayDate = formatInTimezone(
        tripDetails.boardingIso,
        tripDetails.boardingTimezone,
        dl.pattern("weekdayDateYear"),
        dl.locale,
      );
    } else {
      const [y, m, d] = date.split("-").map(Number);
      if (y && m && d) {
        // The SEARCH day, not a resolved boarding date — labelled for the same
        // reason as on the checkout summary.
        displayDate = `${formatDate(new Date(y, m - 1, d), dl.pattern("weekdayDateYear"), { locale: dl.locale })} ${t("trips.routeDeparture", "(route departure)")}`;
      }
    }
  } catch {
    // Keep original if parsing fails
  }

  // Each end of the journey renders in ITS OWN city's zone (PR B3), so the
  // header on this page agrees with the search result the passenger clicked.
  const departureZone = tripDetails?.boardingTimezone ?? tenantTimezone;
  const arrivalZone = tripDetails?.alightingTimezone ?? tenantTimezone;
  const crossesZones = departureZone !== arrivalZone;

  const formatTime = (dateStr: string | null | undefined, zone: string) => {
    if (!dateStr) return "";
    try {
      const base = formatInTimezone(dateStr, zone, "h:mm a");
      // Label the clock only when this journey spans zones.
      return crossesZones
        ? `${base} ${getTimezoneAbbreviation(zone, new Date(dateStr))}`
        : base;
    } catch {
      return "";
    }
  };

  // Handle seat selection/deselection
  const handleSeatClick = useCallback(async (seatNumber: string) => {
    if (!slug || !tripId || !sessionId || isProcessing) return;

    setSeatError(null);

    // Check if seat is already selected (deselect it)
    if (selectedSeats.includes(seatNumber) || ownHeldSeats.includes(seatNumber)) {
      // For now, just deselect locally - in a full implementation we'd release the hold
      setSelectedSeats(prev => prev.filter(s => s !== seatNumber));
      return;
    }

    // Check if we've reached the passenger limit
    if (selectedSeats.length >= passengers) {
      setSeatError({
        type: "default",
        message: t("seats.maxSeatsError", { count: passengers }),
      });
      return;
    }

    // Create hold for the seat
    setIsProcessing(true);
    try {
      const response = await publicApi.post<SeatHoldResponse>(
        `/api/public/${slug}/seat-holds`,
        {
          tripId,
          seatNumber,
          sessionId,
          originCityId: parseInt(fromId, 10),
          destinationCityId: parseInt(toId, 10),
        }
      );

      // Add to selected and held seats
      setSelectedSeats(prev => [...prev, seatNumber]);
      setOwnHeldSeats(prev => [...prev, seatNumber]);
      setHoldExpiresAt(response.expiresAt);

      // Refresh seat data to show updated status
      queryClient.invalidateQueries({ queryKey: ["seats", slug, tripId] });
    } catch (err) {
      // Use error handler utilities for specific error types
      if (isRouteChangedError(err)) {
        // The route / trip / segment changed while the hold waited: another
        // seat will not help — refresh the trip and select again.
        setSeatError({
          type: "conflict",
          title: t("errors.routeChanged.title"),
          message: t("errors.routeChanged.message"),
        });
      } else if (isSeatConflictError(err)) {
        setSeatError({
          type: "conflict",
          title: t("errors.seatConflict.title"),
          message: t("errors.seatConflict.message"),
        });
      } else {
        const parsed = parseError(err);
        // Map parsed error type to the supported types, preserving specific types
        const supportedTypes = ["conflict", "rate-limit", "network", "session"] as const;
        const mappedType = supportedTypes.includes(parsed.type as typeof supportedTypes[number])
          ? (parsed.type as "conflict" | "rate-limit" | "network" | "session")
          : "default";
        setSeatError({
          type: mappedType,
          title: parsed.title,
          message: parsed.message,
          retryAfter: parsed.retryAfter,
        });
      }
      // Refresh to get latest seat status
      refetch();
    } finally {
      setIsProcessing(false);
    }
  }, [slug, tripId, sessionId, selectedSeats, passengers, fromId, toId, isProcessing, queryClient, refetch]);

  const handleBackToResults = () => {
    const params = new URLSearchParams({
      date,
      from: fromId,
      to: toId,
      passengers: passengers.toString(),
      tripType,
    });
    setLocation(`/trips?${params.toString()}`);
  };

  const handleContinueToCheckout = () => {
    // Track seats selected event
    if (tripId) {
      trackEvent("seats_selected", {
        tripId,
        seatCount: selectedSeats.length,
      });
    }

    // Navigate to checkout with selected seats
    const checkoutParams = new URLSearchParams({
      tripId: tripId?.toString() || "",
      seats: selectedSeats.join(","),
      passengers: passengers.toString(),
      tripType,
      date,
      from: fromId,
      to: toId,
      price: price || tripDetails?.segmentPrice || "",
    });
    setLocation(`/checkout?${checkoutParams.toString()}`);
  };

  // Calculate if we can continue
  const canContinue = selectedSeats.length === passengers;
  const seatsRemaining = passengers - selectedSeats.length;

  return (
    <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
      <SEO {...SEOPresets.seatSelection} />

      <EnhancedHeader />

      {/* Route context strip */}
      <section className="bg-white border-b hairline-strong">
        <div className="mx-auto max-w-3xl px-6 py-6 md:py-8">
          <button
            onClick={handleBackToResults}
            className="text-eyebrow uppercase text-ink-muted hover:text-ink inline-flex items-center gap-1.5 mb-3 transition-colors focus-visible-ring"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("seats.backToResults")}
          </button>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="font-display text-[1.5rem] md:text-[1.85rem] leading-tight text-ink">
                {originCity?.name || t("common.loading")}
                <span className="text-brand mx-2">→</span>
                {destinationCity?.name || t("common.loading")}
              </h1>
              <div className="mt-1 text-sm text-ink-muted tabular flex items-center gap-3 flex-wrap">
                <span>{displayDate}</span>
                {tripDetails && (
                  <>
                    <span className="h-1 w-1 bg-ink-subtle" aria-hidden="true" />
                    <span>
                      {formatTime(tripDetails.boardingIso, departureZone)}
                      {/* Gated on the PASSENGER's arrival, not the route's: a
                          known stop-offset arrival vanished whenever the trip
                          row happened to carry no arrivalTime. */}
                      {tripDetails.alightingIso && (
                        <> — {formatTime(tripDetails.alightingIso, arrivalZone)}</>
                      )}
                    </span>
                    <span className="h-1 w-1 bg-ink-subtle" aria-hidden="true" />
                    <span>
                      ${parseFloat(tripDetails.segmentPrice).toFixed(0)} {t("common.perSeat")}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="text-right">
              <div className="text-eyebrow uppercase text-ink-subtle">
                {t("seats.progress", "Selection")}
              </div>
              <div className="font-display text-[1.5rem] text-ink tabular mt-0.5">
                {selectedSeats.length}
                <span className="text-ink-subtle">/{passengers}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main id="main-content" className="flex-1">
        <div className="mx-auto max-w-3xl px-6 py-8">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-ink-muted text-sm">{t("seats.loadingSeats")}</p>
            </div>
          )}

          {error && !isLoading && (
            <div className="text-center py-10 bg-white border hairline-strong">
              <p className="text-destructive mb-4">{t("errors.generic")}</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-ink text-stone-50 hover:bg-brand transition-colors focus-visible-ring"
              >
                {t("common.retry")}
              </button>
            </div>
          )}

          {!isLoading && !error && seatData && (
            <div className="bg-white border hairline-strong shadow-sheet">
              <div className="px-5 md:px-7 py-4 border-b hairline-strong flex items-center justify-between">
                <span className="text-eyebrow uppercase text-ink-muted">
                  {t("seats.title")}
                </span>
                <SeatLegend />
              </div>

              {seatError && (
                <ErrorMessage
                  type={seatError.type}
                  title={seatError.title}
                  message={seatError.message}
                  retryAfter={seatError.retryAfter}
                  onRetry={
                    seatError.type === "conflict" ||
                    seatError.type === "network" ||
                    (seatError.type === "rate-limit" &&
                      (!seatError.retryAfter || seatError.retryAfter <= 0))
                      ? () => {
                          setSeatError(null);
                          refetch();
                        }
                      : undefined
                  }
                  retryText={t("errors.refreshSeats")}
                  className="mx-5 mt-4"
                />
              )}

              <div className="px-4 md:px-8 py-6">
                <SeatGrid
                  seats={seatData.seats}
                  totalSeats={seatData.totalSeats}
                  selectedSeats={selectedSeats}
                  ownHeldSeats={ownHeldSeats}
                  onSeatClick={handleSeatClick}
                  disabled={isProcessing}
                />
              </div>

              {/* Selection + timer + continue footer */}
              <div className="border-t hairline-strong bg-stone-50/60 px-5 md:px-7 py-4 md:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex flex-col">
                  <span className="text-eyebrow uppercase text-ink-subtle">
                    {t("seats.selectedSeats")}
                  </span>
                  <span className="font-display text-[1.15rem] text-ink tabular mt-0.5">
                    {selectedSeats.length > 0
                      ? selectedSeats
                          .sort((a, b) => parseInt(a) - parseInt(b))
                          .join(" · ")
                      : t("seats.noneSelected", "None yet")}
                  </span>
                </div>

                {holdExpiresAt && (
                  <HoldTimer
                    expiresAt={holdExpiresAt}
                    onExpire={() => {
                      setSelectedSeats([]);
                      setOwnHeldSeats([]);
                      setHoldExpiresAt(null);
                    }}
                  />
                )}

                <button
                  className="h-12 px-6 bg-brand text-brand-ink font-semibold text-[0.85rem] uppercase tracking-[0.12em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-ink transition-[background] focus-visible-ring flex items-center justify-center gap-2 w-full sm:w-auto"
                  disabled={!canContinue || isProcessing}
                  onClick={handleContinueToCheckout}
                  data-testid="seats-continue"
                >
                  {isProcessing ? (
                    <>
                      <LoadingSpinner size="sm" />
                      {t("seats.processing")}
                    </>
                  ) : canContinue ? (
                    t("seats.continueToCheckout")
                  ) : (
                    t("seats.selectMore", { count: seatsRemaining })
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t hairline-strong bg-white">
        <div className="mx-auto max-w-3xl px-6 py-5 flex items-center justify-between">
          <span className="text-xs text-ink-subtle">
            {t("common.poweredBy")}
          </span>
          <span className="text-xs text-ink-subtle tabular">
            © {new Date().getFullYear()} {tenant?.name}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default SeatSelectionPage;
